import { useAuthStore } from '@/stores/auth.store';
import { isValidAdminAccessToken, normalizeAdminSession, normalizeToken } from '@/services/auth-token';

const API_URL = import.meta.env.VITE_API_URL || '';

async function readBody(response) {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return { ok: false, code: 'INVALID_RESPONSE', message: await response.text() || `HTTP ${response.status}` };
  }
  return response.json();
}

async function send(path, init = {}, accessToken = '') {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('content-type') && init.body != null) headers.set('content-type', 'application/json');
  const token = normalizeToken(accessToken);
  if (token) headers.set('authorization', `Bearer ${token}`);
  return fetch(`${API_URL}${path}`, { ...init, headers, cache: 'no-store' });
}

export async function api(path, init = {}, options = {}) {
  const store = useAuthStore.getState();
  const authEnabled = options.auth !== false;
  const token = authEnabled ? normalizeToken(options.accessToken ?? store.accessToken) : '';
  const response = await send(path, init, token);
  const body = await readBody(response);
  if (!response.ok || !body.ok) {
    if (response.status === 401 && authEnabled) useAuthStore.getState().clear();
    throw new Error(body.message ?? body.code ?? `HTTP ${response.status}`);
  }
  return body;
}

export async function loginAdmin(credentials) {
  useAuthStore.getState().clear();
  const login = await api('/api/v1/admin-auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  }, { auth: false });

  const session = normalizeAdminSession(login.data);
  if (!isValidAdminAccessToken(session.accessToken, 0)) {
    throw new Error('The server returned an invalid admin access token.');
  }

  const authorization = { accessToken: session.accessToken };
  const [me, overview] = await Promise.all([
    api('/api/v1/admin-auth/me', {}, authorization),
    api('/api/v1/admin/overview', {}, authorization),
  ]);

  return { ...session, admin: me.data, initialOverview: overview.data };
}
